import { httpClient } from "../http/client";

export interface HapTableRequest {
  percentile: number;
  category: string;
  exam?: string;
}

export interface YearlyCutoff {
  year: number;
  category: string;
  cutoff_percentile: number;
}

export interface HapTableCell {
  cutoff_percentile: number;
  deviation: number;
  hap_probability: number;
  yearly_cutoffs?: YearlyCutoff[];
}

export interface HapTableCourse {
  course_name: string;
  course_shortform?: string | null;
}

export interface HapTableCollege {
  college_id?: number;
  college_code: string;
  college_name: string;
  city?: string;
  average_cutoff_percentile: number | null;
  cells: Array<HapTableCell | null>;
}

export interface HapTableResponse { 
  candidate: {
    percentile: number;
    category: string;
    exam?: string;
  };
  courses: HapTableCourse[];
  colleges: HapTableCollege[];
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" ? (value as UnknownRecord) : null;
}

function readString(record: UnknownRecord | null, keys: string[]): string {
  if (!record) return "";
  for (const key of keys) {
    const raw = record[key];
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
  }
  return "";
}

function readOptionalString(record: UnknownRecord | null, keys: string[]): string | undefined {
  const value = readString(record, keys);
  return value || undefined;
}

function readNumber(record: UnknownRecord | null, keys: string[]): number | null {
  if (!record) return null;
  for (const key of keys) {
    const raw = record[key];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string" && raw.trim()) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

function readArray(record: UnknownRecord | null, keys: string[]): unknown[] {
  if (!record) return [];
  for (const key of keys) {
    const raw = record[key];
    if (Array.isArray(raw)) {
      return raw;
    }
  }
  return [];
}

function normalizeCourse(item: unknown): HapTableCourse | null {
  if (typeof item === "string" && item.trim()) {
    return { course_name: item.trim() };
  }

  const record = asRecord(item);
  if (!record) {
    return null;
  }

  const courseName = readString(record, ["course_name", "courseName", "name", "label", "value"]);
  if (!courseName) {
    return null;
  }

  const courseShortform = readOptionalString(record, ["course_shortform", "courseShortform", "shortform"]);

  return {
    course_name: courseName,
    course_shortform: courseShortform ?? null,
  };
}

function normalizeCell(item: unknown): HapTableCell | null {
  const record = asRecord(item);
  if (!record) {
    return null;
  }

  const cutoffPercentile = readNumber(record, ["cutoff_percentile", "cutoffPercentile", "cutoff"]);
  const deviation = readNumber(record, ["deviation", "diff", "delta"]);
  const hapProbability = readNumber(record, ["hap_probability", "hapProbability", "probability"]);

  if (cutoffPercentile === null || deviation === null || hapProbability === null) {
    return null;
  }

  let yearly_cutoffs: YearlyCutoff[] = [];
  const rawCutoffs = readArray(record, ["yearly_cutoffs", "yearlyCutoffs"]);
  if (rawCutoffs.length > 0) {
    yearly_cutoffs = rawCutoffs.map(c => {
      const cRecord = asRecord(c);
      if (!cRecord) return null;
      const year = readNumber(cRecord, ["year"]);
      const category = readString(cRecord, ["category"]);
      const cp = readNumber(cRecord, ["cutoff_percentile", "cutoffPercentile"]);
      if (year === null || !category || cp === null) return null;
      return { year, category, cutoff_percentile: cp };
    }).filter((c): c is YearlyCutoff => c !== null);
  }

  return {
    cutoff_percentile: cutoffPercentile,
    deviation,
    hap_probability: hapProbability,
    yearly_cutoffs: yearly_cutoffs.length > 0 ? yearly_cutoffs : undefined,
  };
}

function normalizeCollege(item: unknown): HapTableCollege | null {
  const record = asRecord(item);
  if (!record) {
    return null;
  }

  const collegeName = readString(record, ["college_name", "collegeName", "name"]);
  if (!collegeName) {
    return null;
  }

  const collegeCode =
    readString(record, ["college_code", "collegeCode", "code"])
    || collegeName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const collegeId = readNumber(record, ["college_id", "collegeId", "id"]) ?? undefined;
  const city = readOptionalString(record, ["city"]);
  const averageCutoffPercentile =
    readNumber(record, ["average_cutoff_percentile", "averageCutoffPercentile", "avg_cutoff_percentile"]);
  const rawCells = readArray(record, ["cells"]);

  return {
    college_id: collegeId,
    college_code: collegeCode,
    college_name: collegeName,
    city,
    average_cutoff_percentile: averageCutoffPercentile,
    cells: rawCells.map((cell) => normalizeCell(cell)),
  };
}

function resolvePayload(raw: unknown): UnknownRecord {
  const root = asRecord(raw);
  if (!root) {
    return {};
  }

  if (readArray(root, ["courses", "course_names"]).length > 0 || readArray(root, ["colleges", "rows", "content"]).length > 0) {
    return root;
  }

  const nestedData = asRecord(root.data);
  if (nestedData) {
    return nestedData;
  }

  const nestedResult = asRecord(root.result);
  if (nestedResult) {
    return nestedResult;
  }

  return root;
}

export function normalizeHapTableResponse(raw: unknown, request: HapTableRequest): HapTableResponse {
  const payload = resolvePayload(raw);
  const candidateRecord =
    asRecord(payload.candidate)
    ?? asRecord(payload.applicant)
    ?? asRecord(payload.user);
  const normalizedCourses = readArray(payload, ["courses", "course_names", "courseNames", "columns"])
    .map((course) => normalizeCourse(course))
    .filter((course): course is HapTableCourse => course !== null);
  const normalizedColleges = readArray(payload, ["colleges", "rows", "content", "table"])
    .map((college) => normalizeCollege(college))
    .filter((college): college is HapTableCollege => college !== null);

  return {
    candidate: {
      percentile: readNumber(candidateRecord, ["percentile", "score", "rank"]) ?? request.percentile,
      category: readString(candidateRecord, ["category", "quota"]) || request.category,
      exam: readOptionalString(candidateRecord, ["exam", "exam_name"]) ?? request.exam,
    },
    courses: normalizedCourses,
    colleges: normalizedColleges,
  };
}

export async function fetchHapTable(payload: HapTableRequest): Promise<HapTableResponse> {
  const response = await httpClient.post<unknown>("/api/v1/applicants/hap/ht_table_v2", payload, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  return normalizeHapTableResponse(response.data, payload);
}

export async function fetchPreferredHapTable(payload: HapTableRequest): Promise<HapTableResponse> {
  const response = await httpClient.post<unknown>("/api/v1/applicants/hap/ht_college_table_v2", payload, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  return normalizeHapTableResponse(response.data, payload);
}
